import React from 'react';
import Button from 'Components/DesignSystem/Button/Button';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Typography from 'Components/DesignSystem/Typography/Typography';

type StopModalProps = {
    isOpen: boolean,
    onCancel: () => void
    handleStop: () => void
}

/**
 * "Do you want to stop the test?" (`okf/design-system/e2e.md` § 160).
 *
 * **This modal makes no backend request** — confirming it closes itself and opens
 * `ABPromoteModal`, which is where the single `finishABTest` POST happens. Nothing is
 * stopped server-side until "Stop & Promote". That is the flow `okf/abtesting/e2e.md`
 * § 5.4 documents and it is unchanged.
 *
 * It was `Components/Common/Modal/ModalContainer` with `width='10%'` — a modal 10% of
 * the viewport wide, which is why the question wrapped onto three lines. More
 * consequentially, `ModalContainer` takes `setOpen` and this call site passed
 * **`handleStop`** to it: so a click on the backdrop, which `ModalContainer` treats as
 * "close", actually CONFIRMED the stop and advanced to the promote modal. Dismissing a
 * confirmation dialog performed the thing it was confirming.
 *
 * DS `Dialog` separates the two: `onClose` is dismissal, and confirming is only ever
 * the footer button. `closeOnOutsideClick` is left at its default of `false` for a
 * destructive confirmation.
 */
const ABStopModal = ({ onCancel, handleStop, isOpen }: StopModalProps) => {
    return (
        <Dialog
            isOpen={isOpen}
            onClose={onCancel}
            title="Stop A/B test"
            tone="warning"
            size="sm"
            data-testid="ab-test-stop-modal"
            footer={
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                    <Button
                        variant="secondary"
                        id="ab-test-stop-modal-cancel-btn"
                        data-testid="ab-stop-cancel-btn"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        id="ab-test-stop-modal-confirm-btn"
                        data-testid="ab-stop-confirm-btn"
                        onClick={handleStop}
                    >
                        Yes, stop
                    </Button>
                </div>
            }
        >
            <Typography variant="body-primary" as="p" className="tw-text-ink">
                Do you want to stop the test?
            </Typography>
        </Dialog>
    );
}

export default ABStopModal;
